
/**
 * Doping (Boost) Pricing Engine
 * Hybrid Model: Base Price + Percentage of Product Price
 * Limits: Min 10 TL, Max 5000 TL
 */

export const DOPING_CONFIG = {
    packages: {
        1: { base: 10, percentage: 0.01, label: '1 Günlük' },
        3: { base: 25, percentage: 0.03, label: '3 Günlük' },
        7: { base: 50, percentage: 0.05, label: '7 Günlük' }
    },
    limits: {
        MIN: 10,
        MAX: 5000,
        HYBRID_THRESHOLD: 500 // Only apply percentage above this price
    }
};

interface DopingRates {
    day1: { base: number; percentage: number };
    day3: { base: number; percentage: number };
    day7: { base: number; percentage: number };
}

/**
 * Calculates the dynamic price for a doping package based on product price.
 */
export const calculateDopingPrice = (productPrice: number, days: 1 | 3 | 7, configRates?: DopingRates): number => {
    // Default to hardcoded if no config rates provided
    let base = DOPING_CONFIG.packages[days].base;
    let percentage = DOPING_CONFIG.packages[days].percentage;

    if (configRates) {
        const key = `day${days}` as keyof DopingRates;
        if (configRates[key]) {
            base = configRates[key].base;
            percentage = configRates[key].percentage;
        }
    }

    let finalPrice = base;

    // Apply hybrid percentage only if product price is significant
    if (productPrice >= DOPING_CONFIG.limits.HYBRID_THRESHOLD) {
        finalPrice = base + (productPrice * percentage);
    }

    // Apply strict limits
    finalPrice = Math.max(finalPrice, DOPING_CONFIG.limits.MIN);
    finalPrice = Math.min(finalPrice, DOPING_CONFIG.limits.MAX);

    return Math.round(finalPrice);
};

/**
 * Calculates the Risk-Free (Commission-based) price.
 * Default: Standard Price * (1 + riskFreeRate)
 */
export const calculateRiskFreePrice = (standardPrice: number, riskFreeRate: number = 0.20): number => {
    return Math.round(standardPrice * (1 + riskFreeRate));
};

/**
 * Provides a human-readable description for the commission-based doping
 */
export const getDopingDescription = (productPrice: number, days: 1 | 3 | 7, configRates?: DopingRates): string => {
    const price = calculateDopingPrice(productPrice, days, configRates);
    const percentage = ((price / productPrice) * 100).toFixed(1);

    if (productPrice < DOPING_CONFIG.limits.HYBRID_THRESHOLD) {
        return `Doping Ücreti: ${price} TL (Sabit)`;
    }

    return `Doping Ücreti: ${price} TL (Ürün fiyatının yaklaşık %${percentage}'i)`;
};
